import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { container } from "tsyringe";
import moment from "moment";
import QuantitySelector from "./QuantitySelector";
import EsimDatePicker from "./EsimDatePicker";
import EsimInfoPopup from "./EsimInfoPopup";
import IconHolaFly from "../../../../../assets/iconHolaFly";
import IconInfoCircle from "../../../../../assets/iconInfoCircle";
import { formatCurrencyWithSymbol } from "../../../../../utils/formatCurrencyWithSymbol";
const EsimAddon = ({ pickupDate, dropoffDate, destinationCountry, currency, exchangeRate, readOnly = false, onEsimChange, t, formatPrice, maxQuantity: maxQuantityProp = 5, esimInfoPopupContent, }) => {
    const [quantity, setQuantity] = useState(0);
    const [startDate, setStartDate] = useState(new Date(pickupDate));
    const [endDate, setEndDate] = useState(new Date(dropoffDate));
    const [unitPriceUsd, setUnitPriceUsd] = useState(0);
    const [unitPriceLocal, setUnitPriceLocal] = useState(0);
    const [available, setAvailable] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [termsAndConditionsUrl, setTermsAndConditionsUrl] = useState();
    const [maxQuantity, setMaxQuantity] = useState(maxQuantityProp);
    const [productId, setProductId] = useState();
    const [conversionRate, setConversionRate] = useState(0);
    const [datesEdited, setDatesEdited] = useState(false);
    // Keep track of last valid state for error rollback (AC #15)
    const lastValidState = useRef({ quantity: 0, unitPriceUsd: 0, unitPriceLocal: 0 });
    const validateEsim = container.resolve("iUseCaseValidateEsim");
    const getRentalDays = useCallback((start, end) => {
        return Math.max(moment(end).diff(moment(start), "days"), 1);
    }, []);
    const buildEsimAddOn = useCallback((qty, priceUsd, priceLocal, rate, prodId) => ({
        esimQuantity: qty,
        esimPriceUsd: priceUsd,
        esimPriceLocal: priceLocal,
        esimTotalUsd: qty * priceUsd,
        esimTotalLocal: qty * priceLocal,
        currency,
        destinationCountry,
        rentalDays: getRentalDays(startDate, endDate),
        productId: prodId,
        conversionRate: rate,
        esimStartDate: startDate.toISOString(),
        esimEndDate: endDate.toISOString(),
        esimDatesEdited: datesEdited,
    }), [currency, destinationCountry, getRentalDays, startDate, endDate, datesEdited]);
    const fetchAvailability = useCallback(async (start, end, currentQuantity) => {
        setLoading(true);
        setError(null);
        const rentalDays = getRentalDays(start, end);
        const qty = currentQuantity !== undefined ? currentQuantity : quantity;
        const response = await validateEsim.handle({
            destinationCountry,
            rentalDays,
            esimQuantity: qty > 0 ? qty : 1,
            currency,
        });
        if (response.available) {
            setAvailable(true);
            setUnitPriceUsd(response.priceUsd);
            setUnitPriceLocal(response.priceLocal);
            setConversionRate(response.conversionRate);
            setProductId(response.productId);
            if (response.termsAndConditionsUrl) {
                setTermsAndConditionsUrl(response.termsAndConditionsUrl);
            }
            if (response.maxQuantity !== undefined) {
                setMaxQuantity(response.maxQuantity);
            }
            // Update last valid state
            lastValidState.current = {
                quantity: qty,
                unitPriceUsd: response.priceUsd,
                unitPriceLocal: response.priceLocal,
            };
        }
        else {
            setAvailable(false);
            setQuantity(0);
            onEsimChange(null);
        }
        setLoading(false);
    }, [destinationCountry, currency, validateEsim, onEsimChange, getRentalDays, quantity]);
    useEffect(() => {
        fetchAvailability(startDate, endDate);
    }, []);
    const handleQuantityChange = useCallback(async (newQuantity) => {
        // AC #14: double-click protection
        if (updating)
            return;
        const previousQuantity = quantity;
        const previousPriceUsd = unitPriceUsd;
        const previousPriceLocal = unitPriceLocal;
        setQuantity(newQuantity);
        setUpdating(true);
        setError(null);
        try {
            const rentalDays = getRentalDays(startDate, endDate);
            const response = await validateEsim.handle({
                destinationCountry,
                rentalDays,
                esimQuantity: newQuantity > 0 ? newQuantity : 1,
                currency,
            });
            if (response.available) {
                setUnitPriceUsd(response.priceUsd);
                setUnitPriceLocal(response.priceLocal);
                setConversionRate(response.conversionRate);
                setProductId(response.productId);
                if (response.termsAndConditionsUrl) {
                    setTermsAndConditionsUrl(response.termsAndConditionsUrl);
                }
                if (response.maxQuantity !== undefined) {
                    setMaxQuantity(response.maxQuantity);
                }
                lastValidState.current = {
                    quantity: newQuantity,
                    unitPriceUsd: response.priceUsd,
                    unitPriceLocal: response.priceLocal,
                };
                if (newQuantity > 0) {
                    onEsimChange(buildEsimAddOn(newQuantity, response.priceUsd, response.priceLocal, response.conversionRate, response.productId));
                }
                else {
                    onEsimChange(null);
                }
            }
            else {
                // AC #15: revert to last valid state on error
                setQuantity(lastValidState.current.quantity);
                setUnitPriceUsd(lastValidState.current.unitPriceUsd);
                setUnitPriceLocal(lastValidState.current.unitPriceLocal);
                setError(t("esim_error_unavailable"));
            }
        }
        catch (_a) {
            // AC #15: revert to last valid state on error
            setQuantity(previousQuantity);
            setUnitPriceUsd(previousPriceUsd);
            setUnitPriceLocal(previousPriceLocal);
            setError(t("esim_error_timeout"));
        }
        setUpdating(false);
    }, [
        updating,
        quantity,
        unitPriceUsd,
        unitPriceLocal,
        destinationCountry,
        currency,
        validateEsim,
        getRentalDays,
        startDate,
        endDate,
        onEsimChange,
        buildEsimAddOn,
        t,
    ]);
    const handleDateApply = useCallback(async (newStart, newEnd) => {
        setShowDatePicker(false);
        setUpdating(true);
        setDatesEdited(true);
        setStartDate(newStart);
        setEndDate(newEnd);
        const rentalDays = getRentalDays(newStart, newEnd);
        const response = await validateEsim.handle({
            destinationCountry,
            rentalDays,
            esimQuantity: quantity > 0 ? quantity : 1,
            currency,
        });
        if (response.available) {
            setUnitPriceUsd(response.priceUsd);
            setUnitPriceLocal(response.priceLocal);
            setConversionRate(response.conversionRate);
            setProductId(response.productId);
            if (response.termsAndConditionsUrl) {
                setTermsAndConditionsUrl(response.termsAndConditionsUrl);
            }
            if (response.maxQuantity !== undefined) {
                setMaxQuantity(response.maxQuantity);
            }
            lastValidState.current = {
                quantity,
                unitPriceUsd: response.priceUsd,
                unitPriceLocal: response.priceLocal,
            };
            if (quantity > 0) {
                onEsimChange(buildEsimAddOn(quantity, response.priceUsd, response.priceLocal, response.conversionRate, response.productId));
            }
        }
        else {
            setError(t("esim_error_unavailable"));
            setQuantity(0);
            onEsimChange(null);
        }
        setUpdating(false);
    }, [
        destinationCountry,
        quantity,
        currency,
        validateEsim,
        getRentalDays,
        onEsimChange,
        buildEsimAddOn,
        t,
    ]);
    const handleDateCancel = () => {
        setShowDatePicker(false);
    };
    if (loading) {
        return (_jsx("div", { className: "esim-addon esim-addon--loading", children: _jsx("div", { className: "esim-addon__skeleton" }) }));
    }
    if (!available) {
        return null;
    }
    const formattedStartDate = moment(startDate).format("MMM DD");
    const formattedEndDate = moment(endDate).format("MMM DD");
    const formattedPrice = formatPrice(unitPriceLocal, currency);
    return (_jsxs("div", { className: "esim-addon", children: [_jsx("div", { className: "esim-addon__header", children: _jsxs("div", { className: "esim-addon__title-row", children: [_jsx(IconHolaFly, {}), _jsx("span", { className: "esim-addon__title", children: t("esim_title") }), _jsx("button", { className: "esim-addon__info-btn", onClick: () => setShowInfoPopup(true), type: "button", "aria-label": "eSIM information", children: _jsx(IconInfoCircle, {}) })] }) }), _jsxs("div", { className: "esim-addon__body", children: [_jsx("div", { className: "esim-addon__description", children: t("esim_description") }), _jsxs("div", { className: "esim-addon__dates-row", children: [_jsxs("span", { className: "esim-addon__dates-label", children: [t("esim_dates_label"), " ", _jsxs("span", { className: "esim-addon__dates-value", children: [formattedStartDate, " - ", formattedEndDate] })] }), !readOnly && (_jsx("button", { className: "esim-addon__edit-btn", onClick: () => setShowDatePicker(true), disabled: updating, type: "button", children: t("esim_edit_dates") }))] }), showDatePicker && !readOnly && (_jsx(EsimDatePicker, { startDate: startDate, endDate: endDate, onApply: handleDateApply, onCancel: handleDateCancel, minDate: new Date(), t: t })), _jsxs("div", { className: "esim-addon__price-row", children: [_jsx("span", { className: "esim-addon__price", children: formatCurrencyWithSymbol(t(currency + "_f"), formattedPrice) }), !readOnly ? (_jsx(QuantitySelector, { value: quantity, min: 0, max: maxQuantity, disabled: updating, onChange: handleQuantityChange })) : (_jsx("span", { className: "esim-addon__counter-qty", children: quantity }))] }), error && (_jsx("div", { className: "esim-addon__error", children: error }))] }), showInfoPopup && (_jsx(EsimInfoPopup, { onClose: () => setShowInfoPopup(false), t: t, termsAndConditionsUrl: termsAndConditionsUrl, content: esimInfoPopupContent }))] }));
};
export default EsimAddon;
